import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidators } from '../../../../_shared/custom-validator';
import { IplexGSyncService } from '../../../services/iplex-g-sync.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestWifiSettingsPram, ResponseWifiSettingsPram } from '../../../models/iplex-g/IPLEX-G';
import { ApplicationError } from '../../../../_errors/application-error';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonConfirmDialogComponent } from '../../../../common-dialog/common-confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { IplexGConnectModeService } from '../../../services/iplex-g-connect-mode.service';
import { CommonSnackBarService } from '../../../../_services/common-snack-bar.service';
import { SpinnerService } from '../../../../_services/spinner.service';
import { CommonSaveConfirmDialogComponent } from '../../../../common-dialog/common-save-confirm-dialog.component';

@Component({
    selector: 'app-iplex-g-wifi-settings-dialog',
    templateUrl: './iplex-g-wifi-settings-dialog.component.html',
    styleUrls: ['./iplex-g-wifi-settings-dialog.component.scss'],
    standalone: false
})
export class IplexGWifiSettingsDialogComponent implements OnInit {

  readonly ssidMaxLength = 16;
  readonly WPAMaxLength = 16;
  readonly WPAMinLength = 8;

  readonly inputPattern = /^[\x20-\x7e]*$/;
  readonly channelOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  readonly securityModeOptions = [
    'NONE',
    'WPA-PSK(TKIP)',
    'WPA-PSK(AES)',
    'WPA2-PSK(TKIP)',
    'WPA2-PSK(AES)'
  ];

  ssidForm = new FormControl<string>({ value: '', disabled: true });
  securityModeForm = new FormControl<string>({ value: '', disabled: true });
  passPhraseForm = new FormControl<string>({ value: '', disabled: true });
  channelForm = new FormControl<number>({ value: null, disabled: true });
  isPassphraseHide = true;

  currentSettings: ResponseWifiSettingsPram;

  onDestroy$ = new Subject();

  get hasFormError() {
    return this.ssidForm.invalid
      || this.securityModeForm.invalid
      || this.passPhraseForm.invalid
      || this.channelForm.invalid;
  }

  get isChanged() {
    if (!this.currentSettings) {
      return false;
    }
    return this.currentSettings.Ssid !== this.ssidForm.value.trim()
      || this.currentSettings.Security !== this.securityModeForm.value
      || this.currentSettings.Passphrase !== this.passPhraseForm.value.trim()
      || this.currentSettings.Channel !== this.channelForm.value;
  }

  get passphraseMaxLength() {
    switch (this.securityModeForm.value) {
      case 'WPA-PSK(TKIP)':
      case 'WPA-PSK(AES)':
      case 'WPA2-PSK(TKIP)':
      case 'WPA2-PSK(AES)':
        return this.WPAMaxLength;
      case 'NONE':
      default:
        return 0;
    }
  }
  get passphraseMinLength() {
    switch (this.securityModeForm.value) {
      case 'WPA-PSK(TKIP)':
      case 'WPA-PSK(AES)':
      case 'WPA2-PSK(TKIP)':
      case 'WPA2-PSK(AES)':
        return this.WPAMinLength;
      case 'NONE':
      default:
        return 0;
    }
  }

  constructor(
    public dialogRef: MatDialogRef<IplexGWifiSettingsDialogComponent>,
    private iplexGSyncService: IplexGSyncService,
    private iplexGConnectModeService: IplexGConnectModeService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private oscSpinnerService: SpinnerService,
    private snackbar: CommonSnackBarService
  ) { }

  ngOnInit(): void {
    this.oscSpinnerService.show(true);
    this.iplexGConnectModeService.isHttpsModeObserver()
      .subscribe(isHttpsMode => {
        if (!isHttpsMode) {
          this.dialogRef.close();
        }
      });

    this.iplexGSyncService.getWiFiSettings()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result=> {
        if (result && result.Response.Result === 'Success') {
          this.currentSettings = result.Response.Data;
          this.initForms(this.currentSettings);
        } else {
          this.currentSettings = null;
          this.snackbar.open(this.translate.instant('iplexGWifiSettingsDialogComponent.errorGetWifiSetting'));
        }
        this.oscSpinnerService.hide(true);
      });
  }

  initForms(param: ResponseWifiSettingsPram) {
    this.ssidForm = new FormControl<string>(
      { value: param.Ssid, disabled: false },
      [
        CustomValidators.requiredTrimmed(),
        Validators.maxLength(this.ssidMaxLength),
        Validators.pattern(this.inputPattern)
      ]
    );

    this.securityModeForm = new FormControl<string>(
      { value: param.Security, disabled: false },
      [CustomValidators.requiredTrimmed()]
    );

    this.channelForm = new FormControl<number>(
      { value: param.Channel, disabled: false },
      [CustomValidators.requiredTrimmed()]
    );

    this.setPassphrase(param.Passphrase);
  }

  setPassphrase(passPhrase: string) {
    switch (this.securityModeForm.value) {
      case 'WPA-PSK(TKIP)':
      case 'WPA-PSK(AES)':
      case 'WPA2-PSK(TKIP)':
      case 'WPA2-PSK(AES)':
        this.passPhraseForm = new FormControl<string>(
          { value: passPhrase, disabled: false },
          [
            CustomValidators.requiredTrimmed(),
            Validators.maxLength(this.passphraseMaxLength),
            Validators.minLength(this.WPAMinLength),
            Validators.pattern(this.inputPattern)
          ]
        );
        break;
      case 'NONE':
      default:
        this.passPhraseForm = new FormControl<string>(
          { value: '', disabled: true },
          []
        );
    }
  }

  apply() {
    if (this.hasFormError) {
      throw new ApplicationError('param invalid');
    }

    const dialogRef = this.openConfirmDialog();
    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.postWifiSettings();
        }
      });
  }

  postWifiSettings() {
    this.oscSpinnerService.show(true);

    const param = new RequestWifiSettingsPram();
    param.Ssid = this.ssidForm.value.trim();
    param.Security = this.securityModeForm.value;
    param.PassPhrase = this.passPhraseForm.value.trim();
    param.Channel = this.channelForm.value;

    this.iplexGSyncService.postWiFiSettings(param)
      .subscribe(
        result => {
          this.oscSpinnerService.hide(true);
          if (result.Response.Result === 'Success') {
            this.dialogRef.close();
            this.snackbar.open(this.translate.instant('iplexGWifiSettingsDialogComponent.successSave'));
            this.iplexGConnectModeService.setHttpsMode(false);
          } else {
            this.snackbar.open(this.translate.instant('iplexGWifiSettingsDialogComponent.updateError'));
          }
        },
        error => {
          this.oscSpinnerService.hide(true);
          console.log(error);
          this.snackbar.open(this.translate.instant('iplexGWifiSettingsDialogComponent.updateError'));
        }
      );
  }

  openConfirmDialog() {
    return this.dialog.open(CommonConfirmDialogComponent, {
      data: {
        firstMessage: this.translate.instant('iplexGWifiSettingsDialogComponent.applyConfirmMassage1'),
        secondMessage: this.translate.instant('iplexGWifiSettingsDialogComponent.applyConfirmMassage2'),
        dialogType: 'confirmation',
        trueBtnText: this.translate.instant('iplexGWifiSettingsDialogComponent.trueBtnText'),
        falseBtnText: this.translate.instant('iplexGWifiSettingsDialogComponent.falseBtnText')
      }
    });
  }

  close() {
    if (!this.isChanged) {
      this.dialogRef.close();
      return;
    }

    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: '450px',
      height: '280px',
      disableClose: true,
      data: { isMove: false, isError: this.hasFormError }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.apply();
      } else if (result !== '') {
        this.dialogRef.close();
      }
    });
  }
}
